import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { FormGroupContainer } from '@/components/FormContainer'
import { FormRow } from '@/components/Form'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { SelectItem } from '@/components/SingleItemSelector'
import {
  JobProgressionBillingModal,
  ProgressionStepSection,
} from '@/features/inventory/admin/components/InventoryItemForm/ManageExtensionBillingInfo/JobProgressionBillingModal'
import { ExtensionPriceInputsContainer } from '@/features/inventory/admin/components/InventoryItemForm/ManageExtensionBillingInfo/ManageExtensionBillingInfoContainer'
import { type JobProgressionBillingModalViewModel } from '@/features/inventory/admin/inventory-item-dialog/JobProgressionBillingModalViewModel'

export interface JobProgressionBillingModalContainerProps {
  vm: JobProgressionBillingModalViewModel
  stepsTestId?: string
  testId?: string
}

export const JobProgressionBillingModalContainer = observer(
  function JobProgressionBillingModalContainer({
    vm,
    stepsTestId,
    testId,
  }: JobProgressionBillingModalContainerProps) {
    const progressionProps = useCustomSelectStateFromItemSource({
      itemSource: useMemo(
        () =>
          ItemSource.static(vm.availableProgressions, (item, text) =>
            item.name.toLowerCase().includes(text.toLowerCase()),
          ),
        [vm.availableProgressions],
      ),
    })

    const progressionStepsProps = useCustomSelectStateFromItemSource({
      itemSource: useMemo(
        () =>
          ItemSource.static(vm.availableSteps, (item, text) =>
            item.name.toLowerCase().includes(text.toLowerCase()),
          ),
        [vm.availableSteps],
      ),
    })

    return (
      <JobProgressionBillingModal
        showing={vm.modal.showing}
        onClose={vm.close}
        onSave={vm.save}
        testId={testId}
        fields={
          <>
            <FormRow>
              <FormGroupContainer
                error={vm.validation.getError('selectedProgression')}
              >
                <CustomSelect
                  {...progressionProps}
                  multi={false}
                  value={vm.selectedProgression}
                  areItemsEqual={(left, right) => left?.value === right?.value}
                  onChange={vm.setSelectedProgression}
                  placeholder="Progression"
                  itemToString={(i) => i.name}
                  renderSearchItem={({ item, ...itemProps }) => (
                    <SelectItem key={item.name} {...itemProps}>
                      {item.name}
                    </SelectItem>
                  )}
                  testId="custom-select"
                />
              </FormGroupContainer>
            </FormRow>

            {vm.selectedProgression && (
              <>
                <FormRow>
                  <FormGroupContainer
                    error={vm.validation.getError('selectedSteps')}
                  >
                    <CustomSelect
                      {...progressionStepsProps}
                      multi={true}
                      value={vm.selectedStepsList}
                      areItemsEqual={(left, right) =>
                        left?.value === right?.value
                      }
                      onChange={vm.setSelectedSteps}
                      placeholder="Steps"
                      itemToString={(i) => i.name}
                      renderSearchItem={({
                        item,
                        ...progressionStepsProps
                      }) => (
                        <SelectItem key={item.name} {...progressionStepsProps}>
                          {item.name}
                        </SelectItem>
                      )}
                      testId="custom-select"
                    />
                  </FormGroupContainer>
                </FormRow>
              </>
            )}
          </>
        }
      >
        {vm.selectedStepsList.map((selectedStep) => (
          <ProgressionStepSection
            key={selectedStep.value}
            name={selectedStep.name}
          >
            <ExtensionPriceInputsContainer
              vm={selectedStep.pricingVm}
              testId={stepsTestId}
            />
          </ProgressionStepSection>
        ))}
      </JobProgressionBillingModal>
    )
  },
)
